import { Check, CheckCircle, Clock, Ban } from 'lucide-react';
import type { TicketRedemptionStatus } from '../api/tickets.api';

/**
 * Starea unei rascumparari, in idiomul vizual al lui ApprovalStatusBadge
 * (`bg-X/10 border-X/20 text-X` + iconita lucide + text - niciodata culoare singura).
 *
 * De ce e componenta noua si nu o extragere din ApprovalStatusBadge, desi idiomul e acelasi:
 * aceea e legata de `LessonAttemptState` si randeaza doar forma bloc. Generalizarea ei ar fi
 * insemnat sa refactorez o componenta vie din raportarea lectiilor, intr-un frontend care NU are
 * niciun test la nivel de componenta (vitest ruleaza pe `environment: node`, fara jsdom). Zero
 * castig pentru utilizator, risc real de regresie invizibila. Idiomul se pastreaza prin
 * construcție; daca cineva scoate vreodata un StatusBadge comun, asta se pliaza pe el.
 *
 * 🔴 CANCELLED e `muted`, NU `destructive`: rosul inseamna „ai de actionat". Anularea e o stare
 * terminala neutra, decisa de ECOTIC - scoala doar o observa.
 */
const CONFIG: Record<TicketRedemptionStatus, {
    icon: typeof CheckCircle; text: string; container: string; text_: string;
}> = {
    REQUESTED: {
        icon: Clock, text: 'Solicitat',
        container: 'bg-card border-warning', text_: 'text-warning',
    },
    // SE-18: starea intermediara (REQUESTED -> CONFIRMED -> FULFILLED). `Check` simplu, nu
    // `CheckCircle` (acela ramane rezervat starii finale, FULFILLED), si `info`, nu `success` -
    // forma SI textul o disting de starea finala, nu doar culoarea (acelasi principiu ca restul
    // fisierului: niciodata culoare singura).
    CONFIRMED: {
        icon: Check, text: 'Confirmat',
        container: 'bg-card border-info', text_: 'text-info',
    },
    FULFILLED: {
        icon: CheckCircle, text: 'Livrat',
        container: 'bg-card border-success', text_: 'text-success',
    },
    CANCELLED: {
        icon: Ban, text: 'Anulat',
        container: 'bg-card border-border', text_: 'text-muted-foreground',
    },
};

interface Props {
    status: TicketRedemptionStatus;
    /** `pill` in liste (compact), `block` pe detaliu (cu aer). */
    variant?: 'pill' | 'block';
}

export function RedemptionStatusBadge({ status, variant = 'pill' }: Props) {
    const c = CONFIG[status];
    const Icon = c.icon;
    const bloc = variant === 'block';
    return (
        <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border ${c.container} ${c.text_} ${
                bloc ? 'p-3 text-base font-semibold' : 'px-2 py-1 text-xs font-medium'
            }`}
        >
            <Icon className={bloc ? 'size-5' : 'size-3.5'} aria-hidden />
            {c.text}
        </span>
    );
}
